import { Timestamp } from 'firebase/firestore';

export const GROUPS_COLLECTION = 'ComicGroups';
export const SERIES_COLLECTION = 'ComicSeries';
export const COMICS_COLLECTION = 'Comics';

export type ComicFormat = 'issue' | 'trade' | 'hardcover' | 'omnibus' | 'graphicNovel';
export type ReadStatus = 'read' | 'unread' | 'started' | 'wantToRead';
export type TrackBy = 'issue' | 'volume';

// Where a comic or series was looked up, so later lookups can find it again.
export interface ExternalId {
  source: 'metron' | 'comicvine' | 'openlibrary';
  id: string;
}

// A shelf of my own making, e.g. "Batman" or "Vertigo".
export interface ComicGroup {
  id: string;
  name: string;
  coverUrl?: string;
  sortOrder: number;
  createdAt: Timestamp;
}

// One run of a title: "Batman (2016)" is a different series to "Batman (2011)".
export interface ComicSeries {
  id: string;
  title: string;
  startYear?: number | null;
  volumeNumber?: number | null;
  publisher?: string;
  groupId: string | null;
  trackBy: TrackBy;
  // Known number of issues or volumes; null while ongoing or unknown
  totalCount?: number | null;
  // Issues published so far, from search; sizes the checklist of an ongoing run
  issueCount?: number | null;
  ongoing: boolean;
  // Marked complete (or caught up, while ongoing); absent = not
  complete?: boolean;
  // On the Wishlist; comes off by itself once reading starts
  wishlisted?: boolean;
  wishlistedAt?: Timestamp | null;
  // Shares the Wishlist order with comics' wantToReadOrder
  wantToReadOrder?: number | null;
  coverUrl?: string;
  externalId?: ExternalId | null;
  // Position within its group's shelf
  sortOrder: number;
  createdAt: Timestamp;
}

// An issue, a trade, an omnibus or a standalone graphic novel.
export interface Comic {
  id: string;
  title: string;
  format: ComicFormat;
  // null = standalone
  seriesId: string | null;
  // Only used by standalones; comics in a series follow the series' group
  groupId: string | null;
  number?: number | null;
  // Order within its series
  readingOrder: number;
  // Position on its group's shelf (standalones only), shared with the series there
  sortOrder: number;
  owned: boolean;
  readStatus: ReadStatus;
  readCount: number;
  lastReadAt?: Timestamp | null;
  // When it was last marked started
  startedAt?: Timestamp | null;
  favourite: boolean;
  wantToReadOrder?: number | null;
  coverUrl?: string;
  writers?: string[];
  artists?: string[];
  publisher?: string;
  notes?: string;
  externalId?: ExternalId | null;
  createdAt: Timestamp;
}

// What the add/edit forms hand back. Every field is set (no undefined),
// because Firestore rejects undefined values.
export type ComicInput = Required<
  Pick<
    Comic,
    | 'title'
    | 'format'
    | 'seriesId'
    | 'groupId'
    | 'number'
    | 'owned'
    | 'readStatus'
    | 'coverUrl'
    | 'writers'
    | 'artists'
    | 'publisher'
    | 'notes'
    | 'externalId'
  >
>;

export type SeriesInput = Required<
  Pick<
    ComicSeries,
    | 'title'
    | 'startYear'
    | 'volumeNumber'
    | 'publisher'
    | 'groupId'
    | 'trackBy'
    | 'totalCount'
    | 'ongoing'
    | 'coverUrl'
    | 'externalId'
    | 'wishlisted'
  >
>;

// A cover on a Library shelf: a whole series, or a standalone comic.
// Ids are prefixed so series and comics can never clash in drag and drop.
export type LibraryTile =
  | { kind: 'series'; id: string; series: ComicSeries; sortOrder: number }
  | { kind: 'comic'; id: string; comic: Comic; sortOrder: number };

export interface LibraryShelf {
  key: string;
  // null for the automatic "Ungrouped series" and "Standalone" shelves
  group: ComicGroup | null;
  title: string;
  meta: string;
  tiles: LibraryTile[];
}

export const seriesTileId = (id: string) => `s:${id}`;
export const comicTileId = (id: string) => `c:${id}`;
export const groupDragId = (id: string) => `g:${id}`;
export const UNGROUPED_SERIES_KEY = 'ungroupedSeries';
export const STANDALONE_KEY = 'standalone';
export const GROUPS_CONTAINER = 'groups';

export type ComicsTab = 'library' | 'reading' | 'toRead' | 'wishlist';
export type ReadFilter = 'all' | 'read' | 'unread';
export type OwnedFilter = 'all' | 'owned' | 'notOwned';
export type SortKey = 'custom' | 'title' | 'added' | 'lastRead';

export const FORMATS: ComicFormat[] = ['issue', 'trade', 'hardcover', 'omnibus', 'graphicNovel'];

// Short labels for chips on covers
export const FORMAT_LABELS: Record<ComicFormat, string> = {
  issue: 'Issue',
  trade: 'TPB',
  hardcover: 'HC',
  omnibus: 'OHC',
  graphicNovel: 'GN',
};

// Full names for menus and forms
export const FORMAT_NAMES: Record<ComicFormat, string> = {
  issue: 'Single issue',
  trade: 'Trade paperback',
  hardcover: 'Hardcover',
  omnibus: 'Omnibus',
  graphicNovel: 'Graphic novel',
};

export const READ_STATUS_LABELS: Record<ReadStatus, string> = {
  read: 'Read',
  unread: 'Unread',
  started: 'Started',
  wantToRead: 'Wishlist',
};

export const SORT_LABELS: Record<SortKey, string> = {
  custom: 'My order',
  title: 'Title (A–Z)',
  added: 'Recently added',
  lastRead: 'Recently read',
};

export const TAB_SORTS: Record<ComicsTab, SortKey[]> = {
  library: ['custom', 'title', 'added'],
  reading: ['lastRead', 'title'],
  toRead: ['added', 'title'],
  wishlist: ['custom', 'title', 'added'],
};

export const EMPTY_MESSAGES: Record<ComicsTab, string> = {
  library: 'No comics yet.',
  reading: 'Nothing on the go. Tap a cover\'s eye or a checklist number to mark it started.',
  toRead: 'Nothing owned and unread. Time to go shopping.',
  wishlist: 'Your wishlist is empty.',
};

/* Tab filters. Buying a wishlist comic moves it to To Read, so they never overlap. */
export const isToRead = (comic: Comic) => comic.owned && comic.readStatus !== 'read';
export const isRead = (comic: Comic) => comic.readStatus === 'read';
export const isStarted = (comic: Comic) => comic.readStatus === 'started';
export const isWantToRead = (comic: Comic) => comic.readStatus === 'wantToRead';
export const isWishlist = (comic: Comic) => isWantToRead(comic) && !comic.owned;
// What a tap on the read toggle does next: not read → started → read → not read
export const nextReadLabel = (status: ReadStatus) =>
  status === 'read' ? 'Mark unread' : status === 'started' ? 'Mark read' : 'Mark started';

export const isWishlistSeries = (series: ComicSeries) => Boolean(series.wishlisted);

export const unitLabel = (trackBy: TrackBy, count = 2) =>
  trackBy === 'issue' ? (count === 1 ? 'issue' : 'issues') : count === 1 ? 'vol' : 'vols';

// "Batman (2016)", or "Batman (2016) Vol. 3" when there's a volume number.
export const seriesLabel = (series: ComicSeries) =>
  [
    series.title,
    series.startYear ? `(${series.startYear})` : '',
    series.volumeNumber ? `Vol. ${series.volumeNumber}` : '',
  ]
    .filter(Boolean)
    .join(' ');

// Progress is worked out from the comics, never stored.
export const seriesProgress = (series: ComicSeries, comics: Comic[]) => {
  const inSeries = comics.filter((comic) => comic.seriesId === series.id);
  const read = inSeries.filter(isRead).length;
  const started = inSeries.filter(isStarted).length;
  const owned = inSeries.filter((comic) => comic.owned).length;
  const total = Math.max(
    series.totalCount ?? Math.max(series.issueCount ?? 0, inSeries.length),
    read
  );
  return { read, started, owned, total, count: inSeries.length };
};

// Started (one read, or one marked started) but not finished, nor marked complete
export const isInProgress = (series: ComicSeries, comics: Comic[]) => {
  if (series.complete) return false;
  const { read, started, total } = seriesProgress(series, comics);
  return (read > 0 || started > 0) && read < total;
};

// The lowest number marked started, if any
export const firstStarted = (series: ComicSeries, comics: Comic[]) =>
  comics
    .filter((comic) => comic.seriesId === series.id && isStarted(comic) && comic.number != null)
    .map((comic) => comic.number as number)
    .sort((a, b) => a - b)[0];

// An ongoing run is never finished, only caught up with
export const completeLabel = (series: ComicSeries) => (series.ongoing ? 'Caught up' : 'Complete');

// The lowest number not yet read, counting up from 1
export const nextUnread = (series: ComicSeries, comics: Comic[]) => {
  const read = new Set(
    comics
      .filter((comic) => comic.seriesId === series.id && isRead(comic) && comic.number != null)
      .map((comic) => comic.number)
  );
  let next = 1;
  while (read.has(next)) next += 1;
  return next;
};

// How many numbered slots the series checklist shows
export const checklistSize = (series: ComicSeries, comics: Comic[]) =>
  series.totalCount ??
  Math.max(
    series.issueCount ?? 0,
    ...comics
      .filter((comic) => comic.seriesId === series.id)
      .map((comic) => Math.floor(comic.number ?? 0))
  );

export const progressPercent = (read: number, total: number) =>
  total > 0 ? Math.round((read / total) * 100) : 0;

export const byReadingOrder = (a: Comic, b: Comic) =>
  a.readingOrder - b.readingOrder || (a.number ?? 0) - (b.number ?? 0);

// A comma-separated form field to a clean list, and back.
export const toList = (value: string) =>
  value
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

export const fromList = (list?: string[]) => (list ?? []).join(', ');

export const millis = (ts?: Timestamp | null) => ts?.toMillis?.() ?? 0;

export const formatDate = (ts?: Timestamp | null) =>
  ts
    ? ts.toDate().toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '';
